import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_PROTECT_CHECKLIST } from '../../utils/constants';
import { Lock, AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Protect() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Protection & Prevention'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-4141">
      <div className="container mx-auto px-4 py-12" data-id="element-4142">
        {/* Header */}
        <div className="mb-12" data-id="element-4143">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-4144">
            <Lock className="h-3 w-3 mr-2" data-id="element-4145" />
            Domain 3 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4146">
            Protection & Prevention
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4147">
            Implement safeguards like access controls, endpoint security, and
            backups to reduce risk and prevent common attacks.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4148">
          <div className="lg:col-span-2 space-y-12" data-id="element-4149">
            {/* What This Domain Covers */}
            <section data-id="element-4150">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4151">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-4152">
                <li className="flex items-start" data-id="element-4153">
                  <span className="text-cyan-500 mr-2" data-id="element-4154">•</span>
                  Identity and access management (MFA, least privilege)
                </li>
                <li className="flex items-start" data-id="element-4155">
                  <span className="text-cyan-500 mr-2" data-id="element-4156">•</span>
                  Endpoint protection and hardening
                </li>
                <li className="flex items-start" data-id="element-4157">
                  <span className="text-cyan-500 mr-2" data-id="element-4158">•</span>
                  Email security and anti-phishing measures
                </li>
                <li className="flex items-start" data-id="element-4159">
                  <span className="text-cyan-500 mr-2" data-id="element-4160">•</span>
                  Backup and ransomware readiness
                </li>
                <li className="flex items-start" data-id="element-4161">
                  <span className="text-cyan-500 mr-2" data-id="element-4162">•</span>
                  Secure configuration and patch management
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-4163">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4164">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-4165">
                <ControlItem title="Enable MFA for All Critical Accounts" description="Require multi-factor authentication for email, admin accounts, and cloud services." data-id="element-4166" />
                <ControlItem title="Implement 3-2-1 Backup Strategy" description="3 copies of data, 2 different media types, 1 offsite/offline backup." data-id="element-4167" />
                <ControlItem title="Deploy Endpoint Protection" description="Install antivirus/EDR on all laptops, desktops, and servers." data-id="element-4168" />
                <ControlItem title="Enable Automatic Updates" description="Turn on auto-updates for operating systems and critical applications." data-id="element-4169" />
                <ControlItem title="Apply Least Privilege Access" description="Users should only have access to systems and data they need for their job." data-id="element-4170" />
                <ControlItem title="Enable Email Filtering" description="Use spam filters and anti-phishing tools to block malicious emails." data-id="element-4171" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-4172">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4173">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-4174">
                <ControlItem title="Implement Privileged Access Management (PAM)" description="Control and monitor admin account usage with just-in-time access." data-id="element-4175" />
                <ControlItem title="Deploy Email Security Gateway" description="Advanced email filtering with sandboxing and link protection." data-id="element-4176" />
                <ControlItem title="Harden Endpoint Configurations" description="Disable unnecessary services, enable firewalls, encrypt disks." data-id="element-4177" />
                <ControlItem title="Implement Application Whitelisting" description="Only allow approved applications to run on critical systems." data-id="element-4178" />
                <ControlItem title="Test Backup Restoration Monthly" description="Verify backups work by performing test restores regularly." data-id="element-4179" />
                <ControlItem title="Deploy Web Filtering" description="Block access to known malicious sites and high-risk categories." data-id="element-4180" />
                <ControlItem title="Implement Network Segmentation" description="Separate guest WiFi, employee network, and critical systems." data-id="element-4181" />
                <ControlItem title="Enable Full Disk Encryption" description="Encrypt all laptops and mobile devices to protect data if lost or stolen." data-id="element-4182" />
                <ControlItem title="Conduct Quarterly Access Reviews" description="Review and remove unnecessary user permissions and accounts." data-id="element-4183" />
                <ControlItem title="Implement Security Awareness Training" description="Train employees quarterly on phishing, passwords, and social engineering." data-id="element-4184" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4185">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4186">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-4187" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-4188">
                <p className="flex items-start" data-id="element-4189">
                  <span className="text-red-500 mr-2" data-id="element-4190">✗</span>
                  Using SMS for MFA (SIM swap attacks are common - use
                  authenticator apps or hardware keys)
                </p>
                <p className="flex items-start" data-id="element-4191">
                  <span className="text-red-500 mr-2" data-id="element-4192">✗</span>
                  Backing up but never testing restore (backups fail when you
                  need them most)
                </p>
                <p className="flex items-start" data-id="element-4193">
                  <span className="text-red-500 mr-2" data-id="element-4194">✗</span>
                  Giving everyone admin rights (principle of least privilege
                  saves you from malware)
                </p>
                <p className="flex items-start" data-id="element-4195">
                  <span className="text-red-500 mr-2" data-id="element-4196">✗</span>
                  Delaying patches indefinitely (attackers exploit known
                  vulnerabilities within days)
                </p>
                <p className="flex items-start" data-id="element-4197">
                  <span className="text-red-500 mr-2" data-id="element-4198">✗</span>
                  Storing backups on the same network (ransomware encrypts
                  network-accessible backups)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-4199">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4200">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-4201" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-4202">
                <QuickWin day="Day 1-2" action="Enable MFA on all admin accounts and email" data-id="element-4203" />
                <QuickWin day="Day 3-4" action="Set up automated backups with one offline copy" data-id="element-4204" />
                <QuickWin day="Day 5-6" action="Enable automatic updates on all devices" data-id="element-4205" />
                <QuickWin day="Day 7" action="Review and remove unnecessary admin privileges" data-id="element-4206" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4207">
            <Checklist id="org-protect" title="Implementation Checklist" items={ORG_PROTECT_CHECKLIST} data-id="element-4208" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4209">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4210">
                Download Resources
              </h3>
              <Link to="/tools/mfa-passkeys" data-id="element-4211">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4212">
                  <Download className="h-4 w-4 mr-2" data-id="element-4213" />
                  MFA Setup Guide
                </Button>
              </Link>
              <Link to="/tools/backup" data-id="element-4214">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4215">
                  <Download className="h-4 w-4 mr-2" data-id="element-4216" />
                  Backup Strategy Guide
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4217">
                <Button variant="secondary" fullWidth data-id="element-4218">
                  <Download className="h-4 w-4 mr-2" data-id="element-4219" />
                  Hardening Checklists
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4220">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4221">Next Steps</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-4222">
                With protections in place, set up monitoring to detect threats
                early.
              </p>
              <Link to="/organization/detect" data-id="element-4223">
                <Button variant="outline" fullWidth data-id="element-4224">
                  Next: Detection & Monitoring
                  <ArrowRight className="h-4 w-4 ml-2" data-id="element-4225" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-4226">
          <Link to="/organization/identify" data-id="element-4227">
            <Button variant="outline" data-id="element-4228">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4229" />
              Previous: Assets & Risk
            </Button>
          </Link>
          <Link to="/organization/detect" data-id="element-4230">
            <Button data-id="element-4231">
              Next Domain
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-4232" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-4233">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-4234">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-4235">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-4236">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-4237">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-4238">{action}</span>
    </div>;
}